import { SchemaModule, ModuleValue, ModuleHasBmi, ModuleHasProms } from './module';
import { JSONSchema } from '../types/json-schema';
import { FormComponent } from '../form.component';
import { DATE_DAY } from '../constants';

export abstract class ModuleValueGeneral extends ModuleValue implements ModuleHasBmi, ModuleHasProms {
  body_weight?: number;
  body_height?: number;
  is_metric?: boolean;
  body_weight_imperial?: number;
  body_height_imperial_ft?: number;
  body_height_imperial_in?: number;
  bmi?: number;
  bmi_compute?: boolean;

  proms?: {
    id?: string;
    message?: string;
    follow_up_7?: boolean;
    follow_up_30_and_180?: boolean;
    patient_name?: string;
    patient_phone?: string;
  };
};

export abstract class SchemaModuleGeneral<T extends ModuleValueGeneral = ModuleValueGeneral> extends SchemaModule<T> {

  private _convertingWeight = false;
  private _convertingHeight = false;
  private _heightConversionTimeout: any = null;
  private _weightConversionTimeout: any = null;
  private _heightMetricConversionTimeout: any = null;
  private _weightMetricConversionTimeout: any = null;

  /**
   * Called when parent form has finished loading. Used to set up custom value listeners.
   * @param form Parent form component
   */
  hook(form: FormComponent) {
    super.hook(form);

    this.listenValue('bmi_compute', this.fieldHandlerBMICompute.bind(this));
    this.listenValue(['body_weight', 'body_height'], this.fieldHandlerBMIWeightHeight.bind(this));
    // this.listenValue('bmi', this.fieldHandlerBMI.bind(this));

    // Imperial to Metric conversions
    this.listenValue('body_weight_imperial', this.fieldHandlerImperialWeightToMetric.bind(this));
    this.listenValue(['body_height_imperial_ft', 'body_height_imperial_in'], this.fieldHandlerImperialHeightToMetric.bind(this));

    // Metric to Imperial conversions
    this.listenValue('body_weight', this.fieldHandlerMetricWeightToImperial.bind(this));
    this.listenValue('body_height', this.fieldHandlerMetricHeightToImperial.bind(this));
  }

  fieldHandlerCopyValue<K extends keyof T>(destinationProperty: K) {
    return (value: T[ K ]) => {
      if (!this.getValue(destinationProperty)) {
        this.updateValue(destinationProperty, value);
      }
    }
  }

  fieldHandlerBMICompute(compute: boolean) {
    if (typeof compute === 'undefined' || compute === null || this.schema.properties.bmi_compute.readOnly) {
      return;
    }

    this.schema.properties['bmi'].readOnly = compute;
    this.schema.properties['body_weight'].readOnly = !compute;
    this.schema.properties['body_height'].readOnly = !compute;
    this.schema.properties['body_weight_imperial'].readOnly = !compute;
    this.schema.properties['body_height_imperial_ft'].readOnly = !compute;
    this.schema.properties['body_height_imperial_in'].readOnly = !compute;

    this.fieldHandlerBMIWeightHeight(this.getValue('body_weight'), this.getValue('body_height'));
  }

  fieldHandlerBMIWeightHeight(weight_kg: number, height_cm: number) {
    const real_weight_kg = this.getValue('body_weight');
    const real_height_cm = this.getValue('body_height');

    if (
      real_weight_kg === undefined || real_weight_kg === null
      || real_height_cm === undefined || real_height_cm === null
    ) {
      return;
    }

    if (this.getValue('bmi_compute')) {
      this.calculateBMI(real_weight_kg, real_height_cm);
    }
  }

  // fieldHandlerBMI(bmi: number) {
  //   if (!this.getValue('bmi_compute')) {
  //     this.updateValue('body_weight', null);
  //     this.updateValue('body_height', null);
  //     this.updateValue('body_weight_imperial', null);
  //     this.updateValue('body_height_imperial_ft', null);
  //     this.updateValue('body_height_imperial_in', null);
  //   }
  // }

  fieldHandlerImperialWeightToMetric(weight_lbs: number) {
    if (weight_lbs === undefined || weight_lbs === null || this._convertingWeight || this.getValue('is_metric')) {
      return;
    }

    if (this._weightMetricConversionTimeout) {
      clearTimeout(this._weightMetricConversionTimeout);
    }

    // Debounce the conversion by 100ms to handle rapid typing
    this._weightMetricConversionTimeout = setTimeout(() => {
      this._convertingWeight = true;

      if (weight_lbs && weight_lbs > 0) {
        const weight_kg = Math.round(weight_lbs / 2.20462 * 10) / 10; // Round to 1 decimal place
        if (this.getValue('body_weight') !== weight_kg) {
          this.updateValue('body_weight', weight_kg);
        }
      } else if (this.getValue('body_weight') !== null) {
        this.updateValue('body_weight', null);
      }

      this._convertingWeight = false;
    }, 300);

  }

  fieldHandlerImperialHeightToMetric(feet: number, inches: number) {
    if (
      (feet === undefined && inches === undefined)
      || (feet === null && inches === null)
      || this._convertingHeight || this.getValue('is_metric')
    ) {
      return;
    }

    if (this._heightMetricConversionTimeout) {
      clearTimeout(this._heightMetricConversionTimeout);
    }

    // Debounce the conversion by 100ms to handle rapid typing
    this._heightMetricConversionTimeout = setTimeout(() => {
      this._convertingHeight = true;

      // Only convert if we have complete valid data
      if ((feet !== null && feet >= 0) || (inches !== null && inches >= 0)) {
        const totalInches = (feet || 0) * 12 + (inches || 0);
        const height_cm = Math.round(totalInches * 2.54);

        if (this.getValue('body_height') !== height_cm) {
          this.updateValue('body_height', height_cm);
        }
      } else if (feet === null && inches === null) {
        if (this.getValue('body_height') !== null) {
          this.updateValue('body_height', null);
        }
      }

      this._convertingHeight = false;
    }, 300);
  }

  fieldHandlerMetricWeightToImperial(weight_kg: number) {
    if (weight_kg === undefined || weight_kg === null || this._convertingWeight || !this.getValue('is_metric')) {
      return;
    }

    // Clear existing timeout to prevent multiple rapid conversions
    if (this._weightConversionTimeout) {
      clearTimeout(this._weightConversionTimeout);
    }

    // Debounce the conversion by 100ms to handle rapid typing
    this._weightConversionTimeout = setTimeout(() => {
      this._convertingWeight = true;

      if (weight_kg && weight_kg > 0) {
        const weight_lbs = Math.round(weight_kg * 2.20462 * 10) / 10; // Round to 1 decimal place
        if (this.getValue('body_weight_imperial') !== weight_lbs) {
          this.updateValue('body_weight_imperial', weight_lbs);
        }
      } else if (this.getValue('body_weight_imperial') !== null) {
        this.updateValue('body_weight_imperial', null);
      }

      this._convertingWeight = false;
    }, 300);

  }

  fieldHandlerMetricHeightToImperial(height_cm: number) {
    if (height_cm === undefined || height_cm === null || this._convertingHeight || !this.getValue('is_metric')) {
      return;
    }

    // Clear existing timeout to prevent multiple rapid conversions
    if (this._heightConversionTimeout) {
      clearTimeout(this._heightConversionTimeout);
    }

    // Debounce the conversion by 100ms to handle rapid typing
    this._heightConversionTimeout = setTimeout(() => {
      this._convertingHeight = true;

      if (height_cm !== null && height_cm > 0) {
        const totalInches = Math.round(height_cm / 2.54);
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches % 12;

        if (this.getValue('body_height_imperial_ft') !== feet || this.getValue('body_height_imperial_in') !== inches) {
          this.updateValue('body_height_imperial_ft', feet);
          this.updateValue('body_height_imperial_in', inches);
        }
      } else if (height_cm === null || height_cm === 0) {
        if (this.getValue('body_height_imperial_ft') !== null || this.getValue('body_height_imperial_in') !== null) {
          this.updateValue('body_height_imperial_ft', null);
          this.updateValue('body_height_imperial_in', null);
        }
      }

      this._convertingHeight = false;
    }, 300);
  }

  calculateBMI(weight_kg: number, height_cm: number) {
    let bmi = null;
    const height_m = height_cm / 100;

    const bmiSchema = this.getProperty('bmi').schema as JSONSchema;

    // Clear previous message
    bmiSchema.description = '';

    if (weight_kg === null && height_cm === null) {
      // No values

    } else if (weight_kg === null || height_cm === null) {
      // Show info message about partial completion
      bmiSchema.description = this.generateMessage('%0 calculation requires both %1 and %2', 'bmi', 'body_weight', 'body_height');

    } else if (weight_kg > 0 && height_m > 0) {

      // Calculate the BMI
      bmi = Math.round(weight_kg / (height_m ** 2));

      if (isNaN(bmi) || !isFinite(bmi)) {
        bmi = null;
      }

      // Show out-of-range messages
      if (bmi < 18.5) {
        bmiSchema.description = 'Low';
      } else if (bmi > 30) {
        bmiSchema.description = 'Very high';
      } else if (bmi > 25) {
        bmiSchema.description = 'High';
      }
    }
    this.updateValue('bmi', bmi);
  }

  fieldHandlerDischargeDate<K extends keyof T>(lengthOfStayProperty: K) {
    return (dischargeDateString: string, dateOfSurgeryString: string) => {
      let dateOfSurgery = new Date(dateOfSurgeryString);
      let dischargeDate = new Date(dischargeDateString);
      let lengthOfStay = Math.ceil((dischargeDate.valueOf() - dateOfSurgery.valueOf()) / DATE_DAY);
      if (isNaN(lengthOfStay.valueOf()) || lengthOfStay < 0) {
        lengthOfStay = null;
      }
      this.updateValue(lengthOfStayProperty, lengthOfStay as any);
    };
  }

}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/forms/module-general.ts